/**
 * Jubly Calendar Application
 * Google Calendar-inspired layout with unified toolbar
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth.jsx';
import { useCalendars, useEvents, useEvent, useCalendarView } from '../hooks/useCalendar.js';
import CalendarSidebar from '../components/calendar/CalendarSidebar.jsx';
import MonthView from '../components/calendar/MonthView.jsx';
import WeekView from '../components/calendar/WeekView.jsx';
import DayView from '../components/calendar/DayView.jsx';
import AgendaView from '../components/calendar/AgendaView.jsx';
import EventModal from '../components/calendar/EventModal.jsx';
import EventDetail from '../components/calendar/EventDetail.jsx';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  Menu,
  X
} from 'lucide-react';

const VIEW_OPTIONS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'agenda', label: 'Schedule' },
];

export default function CalendarApp() {
  const { user: currentUser } = useAuth();
  const {
    calendars,
    sharedCalendars,
    loading: calendarsLoading,
    refresh: refreshCalendars,
    createCalendar,
    updateCalendar
  } = useCalendars();

  const {
    view,
    setView,
    currentDate,
    selectedEventId,
    setSelectedEventId,
    goToToday,
    goToPrevious,
    goToNext,
    getDateRange
  } = useCalendarView();

  const dateRange = getDateRange();
  const {
    events,
    loading: eventsLoading,
    refresh: refreshEvents,
    createEvent,
    updateEvent,
    deleteEvent
  } = useEvents(dateRange);

  const {
    event: selectedEvent,
    addAttendees,
    removeAttendee
  } = useEvent(selectedEventId);

  // UI state
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isEventDetailOpen, setIsEventDetailOpen] = useState(false);
  const [modalInitialDate, setModalInitialDate] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [modalEndDate, setModalEndDate] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter events by visible calendars
  const visibleCalendarIds = calendars
    .filter(c => c.is_visible !== false)
    .map(c => c.id);

  const filteredEvents = events.filter(e =>
    visibleCalendarIds.includes(e.calendar_id)
  );

  // Handlers
  const handleDateSelect = useCallback((date) => {
    setModalInitialDate(date);
    setIsEventModalOpen(true);
  }, []);

  const handleEventClick = useCallback((event) => {
    setSelectedEventId(event.id);
    setIsEventDetailOpen(true);
  }, [setSelectedEventId]);

  const handleCreateEvent = useCallback((dateInfo) => {
    setEditingEvent(null);
    setModalInitialDate(dateInfo?.startDate || new Date());
    setIsEventModalOpen(true);
  }, []);

  const handleEditEvent = useCallback((event) => {
    setEditingEvent(event);
    setIsEventDetailOpen(false);
    setIsEventModalOpen(true);
  }, []);

  const handleSaveEvent = useCallback(async (eventData) => {
    if (editingEvent) {
      await updateEvent(editingEvent.id, eventData);
    } else {
      await createEvent(eventData);
    }
    refreshEvents();
  }, [editingEvent, createEvent, updateEvent, refreshEvents]);

  const handleDeleteEvent = useCallback(async (eventId) => {
    await deleteEvent(eventId);
    setIsEventDetailOpen(false);
    setSelectedEventId(null);
    refreshEvents();
  }, [deleteEvent, setSelectedEventId, refreshEvents]);

  const handleCalendarToggle = useCallback(async (calendarId, isVisible) => {
    await updateCalendar(calendarId, { isVisible });
    refreshCalendars();
  }, [updateCalendar, refreshCalendars]);

  const handleTimeSlotClick = useCallback((date) => {
    setModalInitialDate(date);
    setEditingEvent(null);
    setIsEventModalOpen(true);
  }, []);

  const handleEventUpdate = useCallback(async (eventId, updates) => {
    await updateEvent(eventId, updates);
    refreshEvents();
  }, [updateEvent, refreshEvents]);

  const handleCreateEventRange = useCallback(({ startDate, endDate }) => {
    setEditingEvent(null);
    setModalInitialDate(startDate);
    // Store end date for the modal to use
    setModalEndDate(endDate);
    setIsEventModalOpen(true);
  }, []);

  // Format header date label based on view
  const getHeaderDateLabel = () => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];

    if (view === 'month') {
      return `${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
    if (view === 'week') {
      const start = new Date(currentDate);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(end.getDate() + 6);

      if (start.getMonth() === end.getMonth()) {
        return `${months[start.getMonth()]} ${start.getFullYear()}`;
      }
      return `${months[start.getMonth()].slice(0, 3)} – ${months[end.getMonth()].slice(0, 3)} ${end.getFullYear()}`;
    }
    if (view === 'day') {
      return `${months[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
    }
    if (view === 'agenda') {
      return `${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
    return '';
  };

  // Loading state
  if (calendarsLoading && !calendars.length) {
    return (
      <div className="flex items-center justify-center bg-white" style={{ height: 'calc(100vh - 56px)' }}>
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading calendar...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white" style={{ height: 'calc(100vh - 56px)' }}>
      {/* Google Calendar-style header */}
      <header className="flex items-center h-16 px-4 border-b border-gray-200 bg-white flex-shrink-0">
        {/* Left: hamburger + logo + nav */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Toggle sidebar"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>

          <div className="flex items-center gap-3 ml-1">
            <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {new Date().getDate()}
              </span>
            </div>
            <span className="text-[22px] text-gray-700 font-normal hidden md:block">Calendar</span>
          </div>
        </div>

        {/* Center: navigation */}
        <div className="flex items-center gap-4 ml-8">
          <button
            onClick={goToToday}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Today
          </button>

          <div className="flex items-center">
            <button
              onClick={goToPrevious}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={goToNext}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <h1 className="text-[22px] text-gray-700 font-normal whitespace-nowrap">
            {getHeaderDateLabel()}
          </h1>

          {eventsLoading && (
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          )}
        </div>

        {/* Right: search + view switcher */}
        <div className="ml-auto flex items-center gap-3">
          {/* Search */}
          {searchOpen ? (
            <div className="flex items-center bg-gray-100 rounded-lg px-3 py-1.5">
              <Search className="w-4 h-4 text-gray-500 mr-2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search events"
                className="bg-transparent text-sm outline-none w-48"
                autoFocus
              />
              <button onClick={() => { setSearchOpen(false); setSearchQuery(''); }}>
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <Search className="w-5 h-5 text-gray-600" />
            </button>
          )}

          {/* View switcher - segmented control */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {VIEW_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setView(opt.key)}
                className={`
                  px-3 py-1.5 text-sm font-medium rounded-md transition-all
                  ${view === opt.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                  }
                `}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Body: sidebar + calendar */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        {sidebarOpen && (
          <CalendarSidebar
            calendars={calendars}
            sharedCalendars={sharedCalendars}
            currentDate={currentDate}
            selectedDate={currentDate}
            onDateSelect={handleDateSelect}
            onCalendarToggle={handleCalendarToggle}
            onCalendarEdit={(cal) => {}}
            onCreateCalendar={() => {}}
            onCreateEvent={handleCreateEvent}
          />
        )}

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {view === 'month' && (
            <MonthView
              currentDate={currentDate}
              events={filteredEvents}
              calendars={calendars}
              onDateSelect={handleDateSelect}
              onEventClick={handleEventClick}
              onCreateEvent={handleCreateEvent}
            />
          )}

          {view === 'week' && (
            <WeekView
              currentDate={currentDate}
              events={filteredEvents}
              calendars={calendars}
              onEventClick={handleEventClick}
              onTimeSlotClick={handleTimeSlotClick}
              onEventUpdate={handleEventUpdate}
              onCreateEventRange={handleCreateEventRange}
            />
          )}

          {view === 'day' && (
            <DayView
              currentDate={currentDate}
              events={filteredEvents}
              calendars={calendars}
              onEventClick={handleEventClick}
              onTimeSlotClick={handleTimeSlotClick}
              onEventUpdate={handleEventUpdate}
              onCreateEventRange={handleCreateEventRange}
            />
          )}

          {view === 'agenda' && (
            <AgendaView
              currentDate={currentDate}
              events={filteredEvents}
              calendars={calendars}
              onEventClick={handleEventClick}
            />
          )}
        </div>
      </div>

      {/* Event Modal */}
      <EventModal
        isOpen={isEventModalOpen}
        onClose={() => {
          setIsEventModalOpen(false);
          setEditingEvent(null);
          setModalInitialDate(null);
          setModalEndDate(null);
        }}
        onSave={handleSaveEvent}
        event={editingEvent}
        initialDate={modalInitialDate}
        initialEndDate={modalEndDate}
        calendars={calendars}
      />

      {/* Event Detail */}
      <EventDetail
        event={selectedEvent}
        isOpen={isEventDetailOpen}
        onClose={() => {
          setIsEventDetailOpen(false);
          setSelectedEventId(null);
        }}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
        onAddAttendees={addAttendees}
        onRemoveAttendee={removeAttendee}
        currentUser={currentUser}
      />
    </div>
  );
}
